/**
 * Records which arm of a Shopify Rollouts experiment served the session by
 * writing it to cart attributes, so it ends up on the order.
 *
 * Shopify exposes the assignment in two places, both carrying the same values:
 *
 *   1. The web pixels manager loader tag, as JSON strings —
 *      <script src="https://.../cdn/wpm/....js"
 *              data-rollout-ids="[9109673]"
 *              data-rollout-treatment-ids="[18251945]">
 *   2. Trekkie's config, as real arrays —
 *      ShopifyAnalytics.lib.config.S2S.{rolloutIds,rolloutTreatmentIds}
 *
 * The loader tag is read first because it is in the parsed head and therefore
 * available immediately; ShopifyAnalytics only fills in once trekkie.js has
 * loaded, so it serves as the fallback and is polled for.
 *
 * Both lists are positional — index N of the rollout ids is the experiment,
 * index N of the treatment ids is the arm serving this request. A session can
 * be in more than one rollout at a time, so the values are stored as
 * comma-separated lists plus a paired `id:treatment` list that keeps the
 * association readable on the order.
 *
 * The attributes are only written once the cart holds something — an empty cart
 * never becomes an order, and writing on every pageview would create a cart
 * for every visitor.
 */
(function () {
  'use strict';

  var ROLLOUT_ATTRIBUTE = '_ab_rollout_id';
  var TREATMENT_ATTRIBUTE = '_ab_treatment_id';
  var PAIR_ATTRIBUTE = '_ab_treatment';
  var STORAGE_KEY = 'theme:abTreatmentSynced';

  var LOADER_SELECTOR = 'script[data-rollout-treatment-ids]';
  var READY_TIMEOUT = 5000;
  var POLL_INTERVAL = 100;

  var routes = (window.theme && window.theme.routes) || {};
  var cartUrl = routes.cart_url || '/cart';
  var cartUpdateUrl = routes.cart_update_url || '/cart/update';
  var cartAddUrl = routes.cart_add_url || '/cart/add';

  var assignment = null;
  var inFlight = false;

  // Accepts either source's shape: a JSON string from the loader tag, or a
  // real array from ShopifyAnalytics.
  function normalizeIds(value) {
    var list = value;

    if (typeof list === 'string') {
      try {
        list = JSON.parse(list);
      } catch (e) {
        return [];
      }
    }

    if (!Array.isArray(list)) return [];

    return list
      .map(function (id) {
        return String(id).trim();
      })
      .filter(Boolean);
  }

  function buildAssignment(rolloutIds, treatmentIds) {
    var rollouts = normalizeIds(rolloutIds);
    var treatments = normalizeIds(treatmentIds);
    if (!treatments.length) return null;

    var pairs = treatments.map(function (treatment, index) {
      var rollout = rollouts[index];
      return rollout ? rollout + ':' + treatment : treatment;
    });

    return {
      rollouts: rollouts.join(','),
      treatments: treatments.join(','),
      pairs: pairs.join(',')
    };
  }

  function fromLoaderTag() {
    var loader = document.querySelector(LOADER_SELECTOR);
    if (!loader) return null;

    return buildAssignment(
      loader.getAttribute('data-rollout-ids'),
      loader.getAttribute('data-rollout-treatment-ids')
    );
  }

  function fromAnalytics() {
    var analytics = window.ShopifyAnalytics;
    var config = analytics && analytics.lib && analytics.lib.config;
    var s2s = config && config.S2S;
    if (!s2s) return null;

    return buildAssignment(s2s.rolloutIds, s2s.rolloutTreatmentIds);
  }

  function resolveAssignment() {
    return fromLoaderTag() || fromAnalytics();
  }

  // Polling covers both sources with one mechanism: a late-injected loader tag
  // and a trekkie bundle that has not finished loading look the same from here.
  function whenAssignmentReady(callback) {
    var found = resolveAssignment();
    if (found) {
      callback(found);
      return;
    }

    var waited = 0;
    var timer = setInterval(function () {
      waited += POLL_INTERVAL;

      var result = resolveAssignment();
      if (result) {
        clearInterval(timer);
        callback(result);
        return;
      }

      if (waited >= READY_TIMEOUT) clearInterval(timer);
    }, POLL_INTERVAL);
  }

  function cartToken() {
    var match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    return match ? match[1] : '';
  }

  // The cart token changes when the cart is emptied or an order completes, so
  // keying the guard on it makes the next cart re-sync on its own.
  function stamp() {
    return cartToken() + '|' + assignment.pairs;
  }

  function alreadySynced() {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === stamp();
    } catch (e) {
      return false;
    }
  }

  function markSynced() {
    try {
      sessionStorage.setItem(STORAGE_KEY, stamp());
    } catch (e) {}
  }

  function matchesCart(attributes) {
    if (!attributes) return false;

    return (
      attributes[ROLLOUT_ATTRIBUTE] === assignment.rollouts &&
      attributes[TREATMENT_ATTRIBUTE] === assignment.treatments &&
      attributes[PAIR_ATTRIBUTE] === assignment.pairs
    );
  }

  function writeAttributes() {
    var attributes = {};
    attributes[ROLLOUT_ATTRIBUTE] = assignment.rollouts;
    attributes[TREATMENT_ATTRIBUTE] = assignment.treatments;
    attributes[PAIR_ATTRIBUTE] = assignment.pairs;

    return fetch(cartUpdateUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    }).then(function () {
      markSynced();
    });
  }

  function sync() {
    if (!assignment || inFlight || alreadySynced()) return;
    inFlight = true;

    fetch(cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (!cart.item_count) return;
        if (matchesCart(cart.attributes)) {
          markSynced();
          return;
        }
        return writeAttributes();
      })
      .catch(function () {})
      .then(function () {
        inFlight = false;
      });
  }

  whenAssignmentReady(function (found) {
    assignment = found;

    if (window.theme) window.theme.abTreatment = assignment;

    sync();
  });

  // Catches the first item landing in an empty cart. Both AJAX add-to-cart
  // paths in theme.js go through fetch; the non-AJAX path redirects, and the
  // sync() above picks it up on the next pageview.
  var originalFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var request = originalFetch.apply(this, arguments);

    if (url.indexOf(cartAddUrl) !== -1) {
      request
        .then(function (response) {
          if (response.ok) sync();
        })
        .catch(function () {});
    }

    return request;
  };
})();
